/* eslint-disable camelcase */
import React, { PureComponent, Fragment } from 'react';
import Hidden from '@material-ui/core/Hidden';
import { Cookies, withCookies } from 'react-cookie';
import styled from 'styled-components';
import { isEmptyObject, safeGet } from '../../core/util/object';
import Mobile from './Mobile';
import Desktop from './Desktop';
import { getDefaultBreakpoint } from '../../core/util/device/screen';
import {
  ACCOUNT_HOME_VIEW,
  ACCOUNT_INSURANCE_VIEW,
  ACCOUNT_PROFILE_VIEW,
  LOGGED_OUT_FIND_CARE_VIEW,
  LOGGED_OUT_ABOUT_VIEW,
  LOGGED_OUT_FOR_PROVIDERS_VIEW,
  LOGGED_OUT_RESOURCES_VIEW,
  ACCOUNT_FIND_CARE_VIEW,
  navigateToView,
} from '../Account/util/views';
import history from '../../core/history';
import { analyticsTrackEvent } from '../../core/analytics';
import { Events, Actions } from '../Account/util/analytics';
import { clearUserInfo } from '../../core/session';
import { isPassiveEventSupported } from '../../core/DOMUtils';
import withAuthentication from '../../core/login/withAuthentication';
import { getInsuranceProfileById, shouldShowInsuranceFeatureBadge } from '../../core/util/account';
import { ELIGIBILITY_REJECT_REASON_MEMBER_NOT_FOUND } from '../../constants/insurance';
import Exclamation from '../SolvPatternLibrary/Icons/Exclamation';
import { isEmptyArray } from '../../core/util/array';
import { APP_INTERACTION_ACTIONS, APP_INTERACTION_TYPES } from '../../constants/index';
const ExclamationIcon = styled(Exclamation)`
  width: 2px;
  margin-left: 1px;
`;

const LATEST_BADGEABLE_INSURANCE_RELEASE = '2019-11-01';

type OwnProps = {
  accountSummary?: any;
  allCookies?: any;
  bottomElement?: React.ReactNode;
  context?: any;
  isConsumerOnly?: boolean;
  noHeaderShadow?: boolean;
  login?: any;
  logoOnly?: boolean;
  openLogInModal?: (...args: any[]) => any;
  openSignUpModal?: (...args: any[]) => any;
  resetLogin?: (...args: any[]) => any;
  selectedTabId?: string;
  showModal?: (...args: any[]) => any;
  userProfile?: any;
  hideHeaderBorder?: boolean;
  isLoggedIn: boolean;
  cookies: any;
  trackAppInteraction?: (...args: any[]) => any;
  isSavingAppInteraction?: boolean;
  handleLogoOnClick?: (...args: any[]) => any;
};

type State = any;

type Props = OwnProps & typeof Header.defaultProps;

class Header extends PureComponent<Props, State> {
  static defaultProps = {
    isConsumerOnly: false,
    logoOnly: false,
  };

  listener: any;

  state = {
    isScrolled: false,
  };

  componentDidMount = () => {
    this.listener = () => window.requestAnimationFrame(this.handleScroll);
    const passiveIfSupported = isPassiveEventSupported();
    window.addEventListener('scroll', this.listener, passiveIfSupported);
  };

  componentWillUnmount = () => window.removeEventListener('scroll', this.listener);

  getLoggedOutTabProperties = () => [
    {
      view: LOGGED_OUT_FIND_CARE_VIEW,
      onClick: this.goToPage(LOGGED_OUT_FIND_CARE_VIEW),
    },
    {
      view: LOGGED_OUT_RESOURCES_VIEW,
      onClick: this.goToPage(LOGGED_OUT_RESOURCES_VIEW),
    },
    {
      view: LOGGED_OUT_ABOUT_VIEW,
      onClick: this.goToPage(LOGGED_OUT_ABOUT_VIEW),
    },
    {
      view: LOGGED_OUT_FOR_PROVIDERS_VIEW,
      onClick: this.goToPage(LOGGED_OUT_FOR_PROVIDERS_VIEW),
    },
  ];

  getLoggedInTabProperties = () => [
    {
      view: ACCOUNT_HOME_VIEW,
      onClick: this.goToPage(ACCOUNT_HOME_VIEW),
    },
    {
      view: ACCOUNT_FIND_CARE_VIEW,
      onClick: this.goToPage(ACCOUNT_FIND_CARE_VIEW),
      badge: this.getUnreadMessageCount(),
    },
    {
      view: ACCOUNT_INSURANCE_VIEW,
      onClick: this.goToPage(ACCOUNT_INSURANCE_VIEW),
      badge: this.getInsuranceBadge(),
    },
    {
      view: ACCOUNT_PROFILE_VIEW,
      onClick: this.goToPage(ACCOUNT_PROFILE_VIEW),
    },
  ];

  getTabProperties = () =>
    this.isConsumerHeader() ? this.getLoggedInTabProperties() : this.getLoggedOutTabProperties();

  getTabId = () => {
    const { selectedTabId, isLoggedIn } = this.props;

    if (selectedTabId) {
      if (selectedTabId === LOGGED_OUT_FIND_CARE_VIEW && isLoggedIn) {
        return ACCOUNT_FIND_CARE_VIEW;
      }

      return selectedTabId;
    }

    const view = safeGet(this.props)('context.route.view');

    if (view) {
      return view;
    }

    return null;
  };

  getUnreadMessageCount = () => safeGet(this.props, 0)('accountSummary.number_of_unread_messages');

  getInsuranceBadge = () => {
    const { accountSummary, userProfile } = this.props;

    if (
      isEmptyObject(userProfile) ||
      isEmptyObject(accountSummary) ||
      isEmptyArray(accountSummary.insurance_profiles)
    ) {
      return null;
    }

    if (
      shouldShowInsuranceFeatureBadge(
        accountSummary,
        userProfile.user_profile_id,
        LATEST_BADGEABLE_INSURANCE_RELEASE
      )
    ) {
      return <ExclamationIcon />;
    }

    const insuranceProfile = getInsuranceProfileById(
      accountSummary.insurance_profiles,
      userProfile.insurance_profile_id
    );

    if (isEmptyObject(insuranceProfile)) {
      return null;
    }

    const { is_eligibility_rejection_error_dismissed, eligibility_reject_reason_type } =
      insuranceProfile;
    const bannerDismissed = is_eligibility_rejection_error_dismissed;

    const condition =
      !bannerDismissed &&
      eligibility_reject_reason_type === ELIGIBILITY_REJECT_REASON_MEMBER_NOT_FOUND;

    return condition ? <ExclamationIcon /> : null;
  };

  dismissInsuranceBadge = () => {
    const { accountSummary, userProfile, isSavingAppInteraction } = this.props;

    if (isEmptyObject(userProfile) || isEmptyObject(accountSummary)) {
      return;
    }

    if (
      shouldShowInsuranceFeatureBadge(
        accountSummary,
        userProfile.user_profile_id,
        LATEST_BADGEABLE_INSURANCE_RELEASE
      ) &&
      !isSavingAppInteraction
    ) {
      analyticsTrackEvent(Events.INSURANCE_BADGE, { action: Actions.BADGE_DIMISS });

      // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      this.props.trackAppInteraction(
        accountSummary.id,
        userProfile.user_profile_id,
        APP_INTERACTION_TYPES.insuranceTab,
        APP_INTERACTION_ACTIONS.view
      );
    }
  };

  isConsumerHeader = () => {
    const { isConsumerOnly, isLoggedIn } = this.props;
    const tabId = this.getTabId();

    if (isConsumerOnly) {
      return true;
    }

    if (isLoggedIn) {
      return true;
    }

    return this.getLoggedInTabProperties().some((tab) => tab.view === tabId);
  };

  goToPage = (view: any) => (event: any) => {
    event.preventDefault();
    analyticsTrackEvent(Events.ACCOUNT_NAVIGATION, {
      action: Actions.TOUCH_PRIMARY_TAB,
      label: view,
    });

    switch (view) {
      case ACCOUNT_HOME_VIEW:
      case ACCOUNT_INSURANCE_VIEW:
      case ACCOUNT_FIND_CARE_VIEW:
      case ACCOUNT_PROFILE_VIEW: {
        if (view === ACCOUNT_INSURANCE_VIEW) {
          this.dismissInsuranceBadge();
        }

        const { userProfile } = this.props;
        const userProfileId = safeGet(userProfile)('user_profile_id');
        if (userProfileId) {
          navigateToView(view, userProfileId);
        } else {
          history.push('/login');
        }

        break;
      }

      case LOGGED_OUT_FIND_CARE_VIEW:
      case LOGGED_OUT_ABOUT_VIEW:
      case LOGGED_OUT_FOR_PROVIDERS_VIEW:
      case LOGGED_OUT_RESOURCES_VIEW:
        navigateToView(view);
        break;

      default:
        break;
    }
  };

  handleLogout = () => {
    const { resetLogin, cookies } = this.props;

    clearUserInfo(cookies);

    resetLogin?.();
  };

  handleScroll = () => {
    const { isScrolled: prevScrolled } = this.state;
    const windowOffset = window.scrollY;
    const isScrolled = windowOffset > 0;

    if (prevScrolled !== isScrolled) this.setState({ isScrolled });
  };

  render() {
    const {
      accountSummary,
      bottomElement,
      logoOnly,
      showModal,
      userProfile,
      openLogInModal,
      openSignUpModal,
      noHeaderShadow,
      hideHeaderBorder,
      isLoggedIn,
      handleLogoOnClick,
    } = this.props;

    return (
      <>
        <Hidden initialWidth="md" xsDown>
          <Desktop
            accountSummary={accountSummary}
            bottomElement={bottomElement}
            handleLogoOnClick={handleLogoOnClick}
            handleLogout={this.handleLogout}
            isLoggedIn={isLoggedIn}
            isScrolled={this.state.isScrolled}
            logoOnly={logoOnly}
            noBorder={hideHeaderBorder}
            openLogInModal={openLogInModal}
            openSignUpModal={openSignUpModal}
            selectedTabId={this.getTabId()}
            showModal={showModal}
            tabProperties={this.getTabProperties()}
            userProfile={userProfile}
          />
        </Hidden>

        <Hidden initialWidth="md" smUp>
          <Mobile
            accountSummary={accountSummary}
            handleLogoOnClick={handleLogoOnClick}
            handleLogout={this.handleLogout}
            isLoggedIn={isLoggedIn}
            isScrolled={this.state.isScrolled}
            logoOnly={logoOnly}
            noBorder={hideHeaderBorder}
            noHeaderShadow={noHeaderShadow}
            openLogInModal={openLogInModal}
            openSignUpModal={openSignUpModal}
            selectedTabId={this.getTabId()}
            tabProperties={this.getTabProperties()}
            userProfile={userProfile}
          />
        </Hidden>
      </>
    );
  }
}

export default withCookies(withAuthentication(Header));
